import React, { useEffect, useMemo, useState } from "react";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import {
  DeleteFriend,
  GetCustomerFriends,
  ICustomerFriend,
  PostFriends,
} from "api/rpc/2024-04/facilityAdmin/customer/friend";

import { showError, showSuccess } from "redux/actions/ui";
import { searchHistoryUpdateSearch } from "redux/actions/searchBars/searchHistory";
import { useAppDispatch } from "hooks/redux";
import { useCustomerContext } from "../../context/CustomerContext";

import Input from "components/form/input";
import Spin from "components/spin/spin";
import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";
import { ButtonNew as Button } from "components/buttonNew";
import "./customerPlayingPartners.scss";

type TPlayingPartnersState = {
  isAddPlayingPartnersOpen: boolean;
  newPlayingPartnerQuery: string;
  playingPartnersList: Array<ICustomerFriend>;
  isRemovePlayerWarningOpen: boolean;
  playerToRemove: ICustomerFriend | null;
  playersToAdd: Array<ICustomerFriend>;
  filterQuery: string;
  hasLoadedPlayers: boolean;
};

export default function CustomerPlayingPartners() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { state: customerContextState } = useCustomerContext();
  const customerId = customerContextState?.customer?.id ?? null;

  const [searchedPlayers, setSearchedPlayers] = useState<Array<ICustomerFriend> | null>([]);
  const [playingPartnersState, setPlayingPartnersState] = useState<TPlayingPartnersState>({
    isAddPlayingPartnersOpen: false,
    newPlayingPartnerQuery: "",
    playingPartnersList: [],
    playersToAdd: [],
    isRemovePlayerWarningOpen: false,
    playerToRemove: null,
    filterQuery: "",
    hasLoadedPlayers: false,
  });

  const excludedCustomerIds = useMemo(
    () =>
      new Set<number>([
        ...playingPartnersState.playingPartnersList.map(partner => partner.id),
        ...playingPartnersState.playersToAdd.map(player => player.id),
        ...(customerId ? [customerId] : []),
      ]),
    [customerId, playingPartnersState.playingPartnersList, playingPartnersState.playersToAdd],
  );

  useEffect(() => {
    void loadPlayingPartners();
  }, [customerId]);

  async function loadPlayingPartners() {
    if (!customerId) {
      return;
    }

    const friendsListRes = await GetCustomerFriends({ customer_id: customerId }, false);
    if (friendsListRes.status !== StatusCode.OK) {
      dispatch(showError(friendsListRes?.message ?? t("Error loading playing partners")));
      return;
    }

    setPlayingPartnersState(previousState => ({
      ...previousState,
      playingPartnersList: friendsListRes.data ?? [],
      hasLoadedPlayers: true,
    }));
  }

  async function handleFriendSelection() {
    if (!customerId) {
      dispatch(showError(t("Customer not loaded")));
      return;
    }

    const { playersToAdd } = playingPartnersState;
    const addFriendsRes = await PostFriends(
      { customer_id: customerId, friend_user_ids: playersToAdd.map(player => player.id) },
      true,
    );

    if (addFriendsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding playing partners")));
      return;
    }

    setPlayingPartnersState(previousState => {
      const existingIds = new Set(previousState.playingPartnersList.map(partner => partner.id));
      return {
        ...previousState,
        playingPartnersList: [
          ...previousState.playingPartnersList,
          ...playersToAdd.filter(player => !existingIds.has(player.id)),
        ],
        isAddPlayingPartnersOpen: false,
        newPlayingPartnerQuery: "",
        playersToAdd: [],
      };
    });

    setSearchedPlayers([]);
    dispatch(showSuccess(t("Successfully added playing partners")));
    void loadPlayingPartners();
  }

  function closeAddFriendModal() {
    setPlayingPartnersState(previousState => ({
      ...previousState,
      isAddPlayingPartnersOpen: false,
      newPlayingPartnerQuery: "",
      playersToAdd: [],
    }));
    dispatch(searchHistoryUpdateSearch("playing-partners", ""));
    setSearchedPlayers([]);
  }

  function cancelRemoveWarning() {
    setPlayingPartnersState(previousState => ({
      ...previousState,
      isRemovePlayerWarningOpen: false,
      playerToRemove: null,
    }));
  }

  function handleRemovePlayer(player: ICustomerFriend) {
    setPlayingPartnersState(previousState => ({
      ...previousState,
      playerToRemove: player,
      isRemovePlayerWarningOpen: true,
    }));
  }

  async function removePlayingPartner() {
    if (!customerId || !playingPartnersState.playerToRemove) {
      return;
    }

    const removeFriendRes = await DeleteFriend(
      { customer_id: customerId, friend_user_id: playingPartnersState.playerToRemove.id },
      true,
    );

    if (removeFriendRes.status !== StatusCode.OK) {
      dispatch(showError(removeFriendRes.message ?? t("Error removing playing partner")));
      return;
    }

    dispatch(showSuccess(t("Successfully removed playing partner")));
    setPlayingPartnersState(previousState => ({
      ...previousState,
      playerToRemove: null,
      isRemovePlayerWarningOpen: false,
    }));
    void loadPlayingPartners();
  }

  function handleAddPlayer(newPlayer: ICustomerFriend) {
    const isAlreadyAdded = playingPartnersState.playersToAdd.some(player => player.id === newPlayer.id);

    if (isAlreadyAdded) {
      dispatch(showError(t("Player already added")));
      return;
    }

    const updatedPlayersToAdd = [...playingPartnersState.playersToAdd, newPlayer];
    const playerInputElement = document.getElementById("addPlayerSearchInput") as HTMLInputElement | null;

    ReactDOM.unstable_batchedUpdates(() => {
      setPlayingPartnersState(previousState => ({
        ...previousState,
        playersToAdd: updatedPlayersToAdd,
        newPlayingPartnerQuery: "",
      }));
      setSearchedPlayers([]);
    });

    playerInputElement?.focus();
  }

  function handleRemoveSelectedPlayer(removePlayer: ICustomerFriend) {
    setPlayingPartnersState(previousState => ({
      ...previousState,
      playersToAdd: previousState.playersToAdd.filter(player => player.id !== removePlayer.id),
    }));
  }

  function handleQueryChange(event: React.ChangeEvent<HTMLInputElement>) {
    setPlayingPartnersState(previousState => ({ ...previousState, newPlayingPartnerQuery: event.target.value }));
  }

  function handleFilterPlayer(event: React.ChangeEvent<HTMLInputElement>) {
    setPlayingPartnersState(previousState => ({ ...previousState, filterQuery: event.target.value }));
  }

  function playerMatchesFilterQuery(player: ICustomerFriend) {
    return (player.full_name ?? "").toUpperCase().includes(playingPartnersState.filterQuery.toUpperCase());
  }

  async function searchCustomers(cancelToken: CancelToken) {
    if (!playingPartnersState.newPlayingPartnerQuery) {
      return [];
    }

    const customerSearchRes = await GetCustomer(
      { search: playingPartnersState.newPlayingPartnerQuery, limit: 25, offset: 0 },
      false,
      cancelToken,
    );

    if (customerSearchRes.status !== StatusCode.OK) {
      if (cancelToken.reason) {
        return null;
      }
      dispatch(showError(t("Error finding player")));
      return [];
    }

    return (customerSearchRes.data ?? []).map(customer => ({
      id: customer.id,
      first_name: customer.first_name ?? "",
      last_name: customer.last_name ?? "",
      full_name: customer.full_name ?? `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim(),
      email: customer.email ?? "",
      phone: customer.phone ?? "",
      initials: customer.initials ?? "",
      customer_type: [] as Array<{ title: string }>,
    }));
  }

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();
    const timeoutId = global.setTimeout(() => {
      if (playingPartnersState.newPlayingPartnerQuery) {
        void loadSearchedPlayers(isMounted, source.token);
      }
    }, 500);

    return () => {
      isMounted = false;
      source.cancel("Cancelled");
      clearTimeout(timeoutId);
      setSearchedPlayers([]);
    };
  }, [playingPartnersState.newPlayingPartnerQuery]);

  async function loadSearchedPlayers(isMounted: boolean, cancelToken: CancelToken) {
    setSearchedPlayers(null);

    const customerSearchResults = await searchCustomers(cancelToken);
    if (!customerSearchResults) {
      return;
    }

    const availablePlayers = customerSearchResults.filter(result => !excludedCustomerIds.has(result?.id ?? -1));

    if (customerSearchResults.length === 0) {
      dispatch(showError(t("User not found")));
    } else if (availablePlayers.length === 0) {
      dispatch(showError(t("Player already added to playing partners")));
    }

    if (isMounted) {
      setSearchedPlayers(availablePlayers);
    }
  }

  const addPlayerModalContent = (
    <>
      <div className="flex justify-center mt-3">
        <div className="customers-playing-partners-add-icon">
          <FontAwesomeIcon icon={["far", "user-plus"]} style={{ color: "#2D4675" }} />
        </div>
      </div>

      <div className="customers-playing-partners-add-intro">
        <h1>{t("Add Player")}</h1>
        <p>{t("Search for a playing partner using an email or phone number")}</p>
      </div>

      <div className="py-3">
        <Input
          labelHidden
          placeholder={t("Search by email or phone number...")}
          type="search"
          onChange={handleQueryChange}
          value={playingPartnersState.newPlayingPartnerQuery}
          id="addPlayerSearchInput"
        />
      </div>

      {playingPartnersState.playersToAdd.length > 0 && (
        <div className="customers-playing-partners-add-list">
          <h1>{t("Add to list")}</h1>
          <p className="customers-playing-partners-add-list-subtext">
            {t("The following users will be added to your playing partner's list:")}
          </p>
          {playingPartnersState.playersToAdd.map((player, index) => (
            <div key={`${player?.id}-${index}`} style={{ margin: "10px 0px" }}>
              <div className="customers-playing-partners-player-card">
                <div className="customers-playing-partners-player-left">
                  <div className="customers-playing-partners-player-image">
                    <div className="client-portal-profile-default-image">
                      <p>
                        {player?.first_name?.charAt(0)?.toUpperCase()}
                        {player?.last_name?.charAt(0)?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="customers-playing-partners-player-name">{player?.full_name ?? ""}</p>
                    <p className="customers-playing-partners-player-detail-sm">{player?.email ?? ""}</p>
                  </div>
                </div>
                <div className="customers-playing-partners-player-right">
                  <button
                    onClick={() => player && handleRemoveSelectedPlayer(player)}
                    className="customers-playing-partners-player-action"
                  >
                    {t("Remove")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {searchedPlayers && searchedPlayers.length > 0 ? (
        <div className="customers-playing-partners-search-results">
          <h1>{t("Players")}</h1>
          {searchedPlayers.map((player, index) => (
            <div key={`${player?.id}-${index}`} style={{ margin: "10px 0px" }}>
              <div
                className="customers-playing-partners-player-left customers-playing-partners-player-add"
                onClick={() => player && handleAddPlayer(player)}
              >
                <div className="customers-playing-partners-player-image">
                  <div className="client-portal-profile-default-image">
                    <p>
                      {player?.first_name?.charAt(0)?.toUpperCase()}
                      {player?.last_name?.charAt(0)?.toUpperCase()}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="customers-playing-partners-player-name">{player?.full_name ?? ""}</p>
                  <p className="customers-playing-partners-player-detail-sm">{player?.email ?? ""}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !searchedPlayers ? (
        <span style={{ display: "flex", justifyContent: "center", marginTop: "5px" }}>
          <Spin />
        </span>
      ) : null}
    </>
  );

  return (
    <div className="customers-playing-partners-container">
      <div className="flex justify-between mt-4 items-center">
        <div className="customers-playing-partners-title">{t("Playing Partners")}</div>

        <Button
          type="default"
          onClick={() =>
            setPlayingPartnersState(previousState => ({ ...previousState, isAddPlayingPartnersOpen: true }))
          }
        >
          <FontAwesomeIcon icon={["far", "plus"]} /> &nbsp; {t("Add Players")}
        </Button>
      </div>

      <div className="customers-playing-partners-search">
        <div className="customers-playing-partners-search-input">
          <Input
            placeholder={t("Search players")}
            type="search"
            onChange={handleFilterPlayer}
            value={playingPartnersState?.filterQuery}
          />
        </div>
      </div>

      <div className="customers-playing-partners-list">
        {playingPartnersState?.hasLoadedPlayers ? (
          <div className="customers-playing-partners-list-items">
            {playingPartnersState.playingPartnersList.filter(playerMatchesFilterQuery).map((player, index) => (
              <div key={`${player?.id}-${index}`} className="customers-playing-partners-player">
                <div className="customers-playing-partners-player-left">
                  <div className="customers-playing-partners-player-image">
                    <div className="client-portal-profile-default-image">
                      <p>
                        {player?.first_name?.charAt(0)?.toUpperCase()}
                        {player?.last_name?.charAt(0)?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="customers-playing-partners-player-name">{player?.full_name ?? ""}</p>
                    <p className="customers-playing-partners-player-detail">{player?.email ?? ""}</p>
                  </div>
                </div>
                <div className="customers-playing-partners-player-right">
                  <button
                    onClick={() => player && handleRemovePlayer(player)}
                    className="customers-playing-partners-player-action"
                  >
                    {t("Remove")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="customers-playing-partners-list-items">
            <span style={{ display: "flex", justifyContent: "center", marginTop: "5px" }}>
              <Spin />
            </span>
          </div>
        )}
      </div>

      <Sheet
        open={playingPartnersState.isAddPlayingPartnersOpen}
        size="small"
        height="flexible"
        title={t("Add Playing Partners")}
        closable
        onCancel={closeAddFriendModal}
        onOk={handleFriendSelection}
        cancelText={t("Cancel")}
        okText={t("Add players")}
        okDisabled={playingPartnersState.playersToAdd.length === 0}
      >
        {addPlayerModalContent}
      </Sheet>

      <Popup
        open={playingPartnersState.isRemovePlayerWarningOpen}
        type="warning"
        title={t("Remove Playing Partner")}
        description={t("Are you sure you want to remove this playing partner?")}
        onCancel={cancelRemoveWarning}
        onOk={removePlayingPartner}
        okText={t("Remove")}
      />
    </div>
  );
}
